import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../utils/api';
import { motion } from 'framer-motion';

interface Inquiry {
    _id: string;
    name: string;
    email: string;
    mobile: string;
    status: string;
    source: string;
    createdAt: string;
    suggestedProjects?: { name: string }[];
}

const BuyerInquiries = () => {
    const { token } = useAuth();
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchInquiries = async () => {
        try {
            const res = await fetch(`${API_BASE}/leads/my`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setInquiries(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching inquiries:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchInquiries();
    }, [token]);

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-900 mb-2">My Inquiries</h2>
                <p className="text-slate-500 font-medium text-sm">Track the status of your property and project requests globally.</p>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                    <i className="fa-solid fa-circle-notch fa-spin text-4xl text-blue-600 mb-4"></i>
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Syncing Inquiry Ledger...</p>
                </div>
            ) : inquiries.length === 0 ? (
                <div className="bg-white rounded-[2rem] p-16 text-center border border-slate-100 shadow-sm">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fa-solid fa-envelope-open-text text-3xl text-slate-200"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">No Inquiries Found</h3>
                    <p className="text-slate-400 max-w-sm mx-auto mb-8 text-sm leading-relaxed font-medium">You haven't sent any inquiries yet. Explore our exclusive property portfolio and start your journey today.</p>
                    <a href="/project-list" className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 inline-block text-sm">
                        Explore Projects
                    </a>
                </div>
            ) : (
                <div className="grid gap-6">
                    {inquiries.map((inq, idx) => (
                        <motion.div
                            key={inq._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="bg-white rounded-[2rem] p-6 border border-slate-50 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row items-start md:items-center justify-between group"
                        >
                            <div className="flex items-center gap-6 mb-4 md:mb-0">
                                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all transform group-hover:scale-105">
                                    <i className="fa-solid fa-building-circle-check text-2xl"></i>
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h4 className="font-black text-slate-900 uppercase tracking-tight">
                                            {inq.suggestedProjects?.[0]?.name || "General Property Inquiry"}
                                        </h4>
                                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                            inq.status === 'New Inquiry' ? 'bg-amber-50 text-amber-600' :
                                            inq.status === 'Booked' ? 'bg-emerald-50 text-emerald-600' :
                                            inq.status === 'Lost' ? 'bg-rose-50 text-rose-600' :
                                            'bg-blue-50 text-blue-600'
                                        }`}>
                                            {inq.status}
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-2">
                                        <i className="fa-regular fa-clock"></i> Sent on {new Date(inq.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 w-full md:w-auto">
                                <div className="flex-1 md:flex-none text-right hidden lg:block">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 italic">Submission Channel</p>
                                    <p className="text-xs font-bold text-slate-800 uppercase tracking-tighter">{inq.source || "Web Portal"}</p>
                                </div>
                                <div className="w-px h-8 bg-slate-100 mx-2 hidden lg:block"></div>
                                <button className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold text-xs hover:bg-black transition-colors flex items-center gap-2 w-full md:w-auto justify-center">
                                    Follow Up <i className="fa-solid fa-arrow-right-long text-[10px]"></i>
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </UserDashboardLayout>
    );
};

export default BuyerInquiries;
